import React, { useEffect, useState, useRef } from "react";
import { 
    Avatar, Button, Divider, Grid, List, ListItem, ListItemAvatar, 
    ListItemText, MenuItem, TextField, Typography, Box, Paper, CircularProgress, Fade
} from "@mui/material";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faTicket, faRedo } from '@fortawesome/free-solid-svg-icons';
import { useParams } from "react-router-dom"; // Ajusta según tu versión de react-router
import { getEventTickets } from "../../../../controllers/company/events/MerchantTicketController";

// Importamos la nueva función del backend


export default function TicketsRaffle({ eventId }) {
    // Si usas React Router v6, quita el useParams y usa el prop eventId directo. 
    // Si usas el ID de la URL, déjalo así:
    const { id } = useParams(); 
    const currentEventId = eventId || id;

    // ESTADOS
    const [tickets, setTickets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Parámetros del Sorteo
    const [ticketStatus, setTicketStatus] = useState('Todos'); // '1' (Emitido), '2' (Validado), 'Todos'
    const [totalWinners, setTotalWinners] = useState(1);
    
    // Resultados y Efectos
    const [winners, setWinners] = useState([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [fakeName, setFakeName] = useState("¿Quién será el ganador?");
    
    // Referencia para limpiar el intervalo de la ruleta
    const rouletteInterval = useRef(null);

    // 1. CARGA DE DATOS INICIAL
    useEffect(() => {
        if (currentEventId) handleGetTickets();
        
        // Limpieza del intervalo al desmontar
        return () => clearInterval(rouletteInterval.current);
    }, [currentEventId]);

    const handleGetTickets = async () => {
        setIsLoading(true);
        try {
            // Usamos la API nueva que trae los datos limpios
            let response = await getEventTickets(currentEventId);
            if (response.success) {
                setTickets(response.data);
            }
        } catch (error) {
            console.error("Error cargando tickets para el sorteo", error);
        }
        setIsLoading(false);
    };

    // 2. LÓGICA DEL SORTEO
    // 2. LÓGICA DEL SORTEO
    const realizarSorteo = () => {
        if (!tickets || tickets.length === 0) {
            alert('No hay boletos registrados para este evento.');
            return;
        }

        // Filtramos asegurándonos de no incluir 'Consumidor Final' ni boletos Anulados (3)
        let ticketsFiltrados = tickets.filter(ticket => 
            ticket.customer_name !== 'Consumidor Final' && 
            ticket.status_code !== 3 && 
            (ticketStatus === 'Todos' || ticket.status_code.toString() === ticketStatus)
        );

        if (ticketsFiltrados.length === 0) {
            alert('No hay participantes válidos con los filtros actuales.');
            return;
        }

        // Pre-calculamos los ganadores
        const ganadoresSeleccionados = [];
        const personasGanadoras = new Set(); // <--- AHORA RASTREAMOS PERSONAS
        let ticketsRestantes = [...ticketsFiltrados];

        while (ganadoresSeleccionados.length < totalWinners && ticketsRestantes.length > 0) {
            const indiceAleatorio = Math.floor(Math.random() * ticketsRestantes.length);
            const ticketPotencial = ticketsRestantes[indiceAleatorio];

            // Verificamos si esta PERSONA ya ganó
            if (!personasGanadoras.has(ticketPotencial.customer_name)) {
                ganadoresSeleccionados.push(ticketPotencial);
                personasGanadoras.add(ticketPotencial.customer_name); // Registramos su nombre
                
                // Removemos TODOS los tickets de esta misma persona de la tómbola
                ticketsRestantes = ticketsRestantes.filter(t => t.customer_name !== ticketPotencial.customer_name);
            } else {
                // Si ya ganó, sacamos este ticket en específico y seguimos girando
                ticketsRestantes.splice(indiceAleatorio, 1);
            }
        }

        if (ganadoresSeleccionados.length === 0) return;

        // ==========================================
        // ¡SHOWTIME! Efecto Ruleta de Suspenso
        // ==========================================
        setIsDrawing(true);
        setWinners([]); 
        
        let counter = 0;
        rouletteInterval.current = setInterval(() => {
            const randomDummy = ticketsFiltrados[Math.floor(Math.random() * ticketsFiltrados.length)];
            setFakeName(randomDummy.customer_name);
            counter++;

            if (counter > 40) { // ~3.2 segundos de suspenso
                clearInterval(rouletteInterval.current);
                setIsDrawing(false);
                setWinners(ganadoresSeleccionados);
            }
        }, 80);
    };

    const reiniciarSorteo = () => {
        setWinners([]);
        setFakeName("¿Quién será el ganador?");
    };

    if (isLoading) return <Box p={5} textAlign="center"><CircularProgress /></Box>;

    return (
        <Box sx={{ maxWidth: 800, margin: '0 auto', mt: 4 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center', backgroundColor: '#fcfcfc' }}>
                
                <FontAwesomeIcon icon={faTrophy} size="3x" color="#FFD700" style={{ marginBottom: 15 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom color="primary.main">
                    Sorteo en Vivo
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={4}>
                    Participantes elegibles: {tickets.filter(t => t.customer_name !== 'Consumidor Final' && t.status_code !== 3).length}
                </Typography>

                {/* PANTALLA DE SORTEO / RULETA */}
                {isDrawing ? (
                    <Box sx={{ py: 8, px: 2, backgroundColor: '#121212', borderRadius: 2, mb: 4, boxShadow: 'inset 0 0 20px #000' }}>
                        <Typography variant="h5" color="#FFD700" mb={2}>Sorteando...</Typography>
                        <Typography variant="h3" fontWeight="bold" color="#fff" sx={{ textTransform: 'uppercase', letterSpacing: 2 }}>
                            {fakeName}
                        </Typography>
                    </Box>
                ) : winners.length > 0 ? (
                    /* PANTALLA DE GANADORES */
                    <Fade in={true} timeout={1000}>
                        <Box sx={{ py: 4, mb: 4 }}>
                            <Typography variant="h5" fontWeight="bold" color="success.main" mb={3}>
                                ¡Felicidades a los ganadores! 🎉
                            </Typography>
                            <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                {winners.map((item, index) => (
                                    <React.Fragment key={item.id}>
                                        <ListItem sx={{ py: 2 }}>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: '#FFD700', color: '#000', fontWeight: 'bold' }}>
                                                    {index + 1}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText 
                                                primary={<Typography variant="h6" fontWeight="bold">{item.customer_name}</Typography>} 
                                                secondary={`Localizador: ${item.uuid.substring(0, 8).toUpperCase()} | Pedido: ${item.order_code}`} 
                                            />
                                        </ListItem>
                                        {index < winners.length - 1 && <Divider component="li" />}
                                    </React.Fragment>
                                ))}
                            </List>
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                startIcon={<FontAwesomeIcon icon={faRedo} />} 
                                onClick={reiniciarSorteo}
                                sx={{ mt: 3 }}
                            >
                                Hacer otro sorteo
                            </Button>
                        </Box>
                    </Fade>
                ) : (
                    /* PANTALLA DE CONFIGURACIÓN */
                    <Grid container spacing={3} justifyContent="center" mb={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Filtro de Boletos"
                                select
                                value={ticketStatus}
                                onChange={(e) => setTicketStatus(e.target.value)}
                                variant="outlined"
                            >
                                <MenuItem value='Todos'>Todos los válidos</MenuItem>
                                <MenuItem value='1'>Solo Emitidos (Aún no entran)</MenuItem>
                                <MenuItem value='2'>Solo Validados (Ya en el evento)</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Cantidad de Ganadores"
                                value={totalWinners}
                                InputProps={{ inputProps: { min: 1, max: 20 } }}
                                onChange={(e) => setTotalWinners(Math.max(1, parseInt(e.target.value) || 1))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                fullWidth
                                size="large"
                                variant="contained"
                                color="primary"
                                onClick={realizarSorteo}
                                sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                                startIcon={<FontAwesomeIcon icon={faTicket} />}
                            >
                                INICIAR SORTEO
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Paper>
        </Box>
    );
}